import { createRoot } from "react-dom/client"
import { placementEngine } from "@theconversation/promos-client"
import throttle from "lodash/throttle"
import groupBy from "lodash/groupBy"
import React from "react"

import { registerPromosSignal } from "./newsletter"
import localStorageCheck from "lib/localStorageCheck"
import Promo from "components/Promo"

// Throttle refresh events to at most one per second.
const REFRESH_RATE = 1000

// Store the roots for each slot, otherwise we remount the component on every render

/**
 * Renders the given promos.
 *
 * @private
 * @param {Object} slot The slot object, composed of the slot ID and its React root.
 * @param {Object} placementsMap A map from slot IDs to promos.
 * @param {Function} onClick The callback function called when a promo is
 * clicked.
 * @param {Function} onClose The callback function called when a promo is
 * closed.
 * @param {Function} onView The callback function called when a promo is viewed.
 * @returns The react element.
 */
function renderSlot(slot, placementsMap, onClick, onClose, onView) {
  const { element, id, root } = slot
  const promos = placementsMap[id] || []
  const promoComponents = promos.map((promo) => (
    <Promo
      key={promo.promoId}
      promo={promo}
      placeholder={element.innerHTML}
      onClick={onClick}
      onClose={onClose}
      onView={onView}
    />
  ))

  root.render(<>{promoComponents}</>)
}

/**
 * Places the list of candidate promos on the page.
 *
 * @param {Array} candidatePromos The list of promos.
 * @param {Object} context A custom context object added to placement engine's context.
 */
export default function renderComponents(candidatePromos, context = {}) {
  let onRefresh
  const storage = localStorageCheck() ? window.localStorage : undefined

  // Grab the slots from the DOM, creating a map of slot IDs and React roots.
  const slotElements = document.getElementsByClassName("slot")
  const slots = Array.from(slotElements).map((slotElement) => {
    return {
      element: slotElement,
      id: parseInt(slotElement.dataset.id, 10),
      root: createRoot(slotElement),
    }
  })

  // Create the placement engine signal.
  const signal = placementEngine(storage, candidatePromos, context)

  // Register this signal to receive newsletter events
  registerPromosSignal(signal)

  // Throttle calls to onRefresh.
  //
  // Scrolling can generate a whole bunch of events, so we need to make sure we
  // don't handle too many of them and kill the browser.
  const handleRefresh = throttle(() => {
    if (onRefresh) {
      onRefresh()
    }
  }, REFRESH_RATE)

  // Listen for window events.
  window.addEventListener("resize", handleRefresh)
  window.addEventListener("scroll", handleRefresh, { passive: true })

  signal.subscribe((e) => {
    // Set the onRefresh callback.
    onRefresh = e.onRefresh

    // Group the promos by slotId to get the placements for each slot.
    const placementsMap = groupBy(e.promos, "slotId")

    // Render the slots.
    slots.forEach((slot) => renderSlot(slot, placementsMap, e.onClick, e.onClose, e.onView))
  })

  return signal
}
